import { ErrorInfo } from 'react';

export interface ErrorLogEntry {
  id: string;
  timestamp: string;
  error: {
    message: string;
    stack?: string | undefined;
    name: string;
  };
  context?: {
    componentStack?: string | undefined;
    errorBoundary?: string | undefined;
    errorInfo?: ErrorInfo | undefined;
    userAgent?: string | undefined;
    url?: string | undefined;
    userId?: string | undefined;
    sessionId?: string | undefined;
  };
  severity: 'low' | 'medium' | 'high' | 'critical';
  tags?: string[];
  metadata?: Record<string, any>;
}

export interface ErrorReportingConfig {
  enabled: boolean;
  maxLogEntries: number;
  logLevel: 'error' | 'warn' | 'info' | 'debug';
  enableConsoleLogging: boolean;
  enableLocalStorage: boolean;
  sessionId?: string;
  userId?: string;
}

class ErrorLogger {
  private config: ErrorReportingConfig;
  private logEntries: ErrorLogEntry[] = [];
  private sessionId: string;

  constructor(config: Partial<ErrorReportingConfig> = {}) {
    this.config = {
      enabled: true,
      maxLogEntries: 100,
      logLevel: 'error',
      enableConsoleLogging: process.env.NODE_ENV === 'development',
      enableLocalStorage: true,
      ...config,
    };

    this.sessionId = this.generateSessionId();
    this.loadPersistedLogs();
  }

  private generateSessionId(): string {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  private loadPersistedLogs(): void {
    if (!this.config.enableLocalStorage) return;

    try {
      const persistedLogs = localStorage.getItem('errorLogs');
      if (persistedLogs) {
        this.logEntries = JSON.parse(persistedLogs);
      }
    } catch (error) {
      console.warn('Failed to load persisted error logs:', error);
    }
  }

  private persistLogs(): void {
    if (!this.config.enableLocalStorage) return;

    try {
      localStorage.setItem('errorLogs', JSON.stringify(this.logEntries));
    } catch (error) {
      console.warn('Failed to persist error logs:', error);
    }
  }

  private cleanupOldLogs(): void {
    if (this.logEntries.length > this.config.maxLogEntries) {
      this.logEntries = this.logEntries.slice(-this.config.maxLogEntries);
    }

    // Remove logs older than 24 hours
    const oneDayAgo = new Date(Date.now() - 24 * 60 * 60 * 1000);
    this.logEntries = this.logEntries.filter(
      entry => new Date(entry.timestamp) > oneDayAgo
    );
  }

  private determineSeverity(
    error: Error
  ): 'low' | 'medium' | 'high' | 'critical' {
    const message = error.message.toLowerCase();
    const stack = error.stack?.toLowerCase() || '';

    // Critical errors
    if (
      message.includes('network error') ||
      message.includes('failed to fetch') ||
      message.includes('internal server error') ||
      stack.includes('chunk load error')
    ) {
      return 'critical';
    }

    // High severity errors
    if (
      message.includes('reference error') ||
      message.includes('type error') ||
      message.includes('syntax error') ||
      stack.includes('react') ||
      stack.includes('component')
    ) {
      return 'high';
    }

    // Medium severity errors
    if (
      message.includes('warning') ||
      message.includes('deprecated') ||
      message.includes('validation')
    ) {
      return 'medium';
    }

    // Default to medium for unknown errors
    return 'medium';
  }

  public logError(
    error: Error,
    context: ErrorLogEntry['context'] = {},
    metadata: Record<string, any> = {}
  ): void {
    if (!this.config.enabled) return;

    const logEntry: ErrorLogEntry = {
      id: `error_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      timestamp: new Date().toISOString(),
      error: {
        message: error.message,
        stack: error.stack,
        name: error.name,
      },
      context: {
        ...context,
        userAgent: navigator.userAgent,
        url: window.location.href,
        userId: this.config.userId,
        sessionId: this.sessionId,
      },
      severity: this.determineSeverity(error),
      tags: this.generateTags(error),
      metadata,
    };

    this.logEntries.push(logEntry);
    this.cleanupOldLogs();
    this.persistLogs();

    // Console logging
    if (this.config.enableConsoleLogging) {
      // eslint-disable-next-line no-console
      // console.group(`🚨 Error Logged - ${logEntry.severity.toUpperCase()}`);
      // eslint-disable-next-line no-console
      // console.error('Error:', error);
      // eslint-disable-next-line no-console
      // console.log('Context:', context);
      // eslint-disable-next-line no-console
      // console.log('Metadata:', metadata);
      // eslint-disable-next-line no-console
      // console.log('Log Entry:', logEntry);
      // eslint-disable-next-line no-console
      // console.groupEnd();
    }

    // In a real application, you would send this to your error reporting service
    this.sendToErrorReportingService(logEntry);
  }

  private generateTags(error: Error): string[] {
    const tags: string[] = [];
    const message = error.message.toLowerCase();
    const stack = error.stack?.toLowerCase() || '';

    // Error type tags
    if (error.name === 'TypeError') tags.push('type-error');
    if (error.name === 'ReferenceError') tags.push('reference-error');
    if (error.name === 'SyntaxError') tags.push('syntax-error');

    // Component tags
    if (stack.includes('react')) tags.push('react');
    if (stack.includes('component')) tags.push('component');

    // Network tags
    if (message.includes('network') || message.includes('fetch'))
      tags.push('network');

    // Browser tags
    if (navigator.userAgent.includes('Chrome')) tags.push('chrome');
    if (navigator.userAgent.includes('Firefox')) tags.push('firefox');
    if (navigator.userAgent.includes('Safari')) tags.push('safari');

    // Environment tags
    tags.push(process.env.NODE_ENV || 'production');

    return tags;
  }

  private async sendToErrorReportingService(
    logEntry: ErrorLogEntry
  ): Promise<void> {
    // This is where you would integrate with external error reporting services
    // Examples: Sentry, LogRocket, Bugsnag, etc.

    // For now, we'll just simulate the API call
    try {
      // Simulate API call
      if (process.env.NODE_ENV === 'development') {
        // eslint-disable-next-line no-console
        console.log('📤 Would send to error reporting service:', {
          service: 'ErrorReportingService',
          payload: {
            id: logEntry.id,
            timestamp: logEntry.timestamp,
            error: logEntry.error,
            severity: logEntry.severity,
            tags: logEntry.tags,
            context: logEntry.context,
          },
        });
      }

      // Example integration patterns:
      //
      // Sentry:
      // Sentry.captureException(error, {
      //   tags: logEntry.tags,
      //   extra: logEntry.metadata,
      //   contexts: { errorBoundary: logEntry.context }
      // });
      //
      // LogRocket:
      // LogRocket.captureException(error);
      //
      // Custom API:
      // await fetch('/api/errors', {
      //   method: 'POST',
      //   headers: { 'Content-Type': 'application/json' },
      //   body: JSON.stringify(logEntry)
      // });
    } catch (reportingError) {
      console.warn(
        'Failed to send error to reporting service:',
        reportingError
      );
    }
  }

  public getErrorLogs(): ErrorLogEntry[] {
    return [...this.logEntries];
  }

  public clearErrorLogs(): void {
    this.logEntries = [];
    this.persistLogs();
  }

  public getErrorSummary(): {
    total: number;
    bySeverity: Record<string, number>;
    recent: ErrorLogEntry[];
  } {
    const bySeverity = this.logEntries.reduce(
      (acc, entry) => {
        acc[entry.severity] = (acc[entry.severity] || 0) + 1;
        return acc;
      },
      {} as Record<string, number>
    );

    const recent = this.logEntries
      .slice(-10)
      .sort(
        (a, b) =>
          new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
      );

    return {
      total: this.logEntries.length,
      bySeverity,
      recent,
    };
  }

  public updateConfig(newConfig: Partial<ErrorReportingConfig>): void {
    this.config = { ...this.config, ...newConfig };
  }
}

// Create a singleton instance
export const errorLogger = new ErrorLogger();

// Export utility functions
export const logError = (error: Error, context?: any, metadata?: any) => {
  errorLogger.logError(error, context, metadata);
};

export const getErrorLogs = () => errorLogger.getErrorLogs();
export const clearErrorLogs = () => errorLogger.clearErrorLogs();
export const getErrorSummary = () => errorLogger.getErrorSummary();
